import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { userService } from '../services/database/userService';
import { User } from '../types/auth';
import {
  Users,
  UserCheck,
  UserX,
  Shield,
  Mail,
  Calendar,
  Search,
  Filter,
  MoreVertical,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Crown,
  Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminPage: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, hasPermission } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'student' | 'teacher' | 'admin'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadUsers(1);
  }, [roleFilter, statusFilter, domainFilter]);

  // Load when page changes (but not when filters change as they reset page to 1)
  useEffect(() => {
    loadUsers(page);
  }, [page]);

  const loadUsers = async (currentPage: number = page) => {
    try {
      setLoading(true);
      const result = await userService.getAllUsers({
        page: currentPage,
        limit: 20,
        role: roleFilter,
        status: statusFilter,
        search: searchTerm,
        source_domain: domainFilter
      });

      setUsers(result.users);
      setTotalUsers(result.total);
      setTotalPages(Math.ceil(result.total / 20));
      if (currentPage !== page) setPage(currentPage);

    } catch (error) {
      console.error('Error loading users:', error);
      // Fallback empty or alert
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'student' | 'teacher' | 'admin') => {
    try {
      await userService.updateUserRole(userId, newRole);
      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, role: newRole, permissions: getDefaultPermissions(newRole) }
          : user
      ));
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Có lỗi xảy ra khi cập nhật quyền người dùng');
    }
  };

  const handleStatusChange = async (userId: string, isActive: boolean) => {
    try {
      await userService.updateUser(userId, { isActive });
      setUsers(users.map(user =>
        user.id === userId ? { ...user, isActive } : user
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Có lỗi xảy ra khi cập nhật trạng thái người dùng');
    }
  };

  const handleProToggle = async (userId: string, currentIsPro: boolean) => {
    try {
      const updatedUser = await userService.updateUser(userId, {
        permissions: { pro: !currentIsPro }
      });
      setUsers(users.map(u => u.id === userId ? { ...u, permissions: { ...u.permissions, pro: !currentIsPro } } : u));
    } catch (error) {
      console.error('Error toggling PRO:', error);
      alert('Có lỗi xảy ra khi cập nhật PRO');
    }
  };

  const getDomainBadge = (domain?: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      ebook:    { label: '📚 Ebook',    cls: 'bg-blue-100 text-blue-800' },
      lab:      { label: '⚗️ Lab',      cls: 'bg-green-100 text-green-800' },
      tronde:   { label: '📝 Trộn đề', cls: 'bg-orange-100 text-orange-800' },
      eng:      { label: '🌐 Eng',      cls: 'bg-purple-100 text-purple-800' },
      vatli365: { label: '🏠 VatLi365', cls: 'bg-gray-200 text-gray-700' },
    };
    return domain && map[domain]
      ? map[domain]
      : { label: 'Chưa rõ', cls: 'bg-gray-100 text-gray-400' };
  };

  const getDefaultPermissions = (role: string): string[] => {
    const permissions = {
      student: ['lessons:read', 'quizzes:read', 'quizzes:submit', 'profile:read', 'profile:write', 'progress:read'],
      teacher: ['lessons:read', 'lessons:write', 'lessons:delete', 'quizzes:read', 'quizzes:write', 'quizzes:delete', 'students:read', 'students:manage', 'profile:read', 'profile:write', 'progress:read', 'progress:manage', 'reports:read', 'reports:write'],
      admin: ['lessons:read', 'lessons:write', 'lessons:delete', 'quizzes:read', 'quizzes:write', 'quizzes:delete', 'users:read', 'users:write', 'users:delete', 'users:manage', 'teachers:approve', 'students:read', 'students:manage', 'profile:read', 'profile:write', 'progress:read', 'progress:manage', 'reports:read', 'reports:write', 'system:manage'],
    };
    return permissions[role as keyof typeof permissions] || permissions.student;
  };

  const getRoleDisplayName = (role: string): string => {
    const roleNames = {
      student: 'Học sinh',
      teacher: 'Giáo viên',
      admin: 'Quản trị viên',
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };

  const getRoleColor = (role: string): string => {
    const colors = {
      student: 'bg-green-100 text-green-800',
      teacher: 'bg-blue-100 text-blue-800',
      admin: 'bg-red-100 text-red-800',
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Không có quyền truy cập</h1>
          <p className="text-gray-600 mb-6">Chỉ quản trị viên mới có thể truy cập trang này.</p>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Quay về trang chủ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quản lý người dùng</h1>
              <p className="text-gray-600 mt-1">Quản lý tài khoản và phân quyền người dùng</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadUsers(page)}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                title="Làm mới danh sách"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <div className="text-sm text-gray-500">
                Tổng: {totalUsers} người dùng
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative md:col-span-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Tìm theo tên, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Domain Filter */}
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              className="px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50 text-blue-800 font-medium"
            >
              <option value="all">🌐 Tất cả nguồn</option>
              <option value="ebook">📚 Ebook</option>
              <option value="lab">⚗️ Lab</option>
              <option value="tronde">📝 Trộn đề</option>
              <option value="eng">🌐 Eng</option>
              <option value="vatli365">🏠 VatLi365</option>
            </select>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tất cả vai trò</option>
              <option value="student">Học sinh</option>
              <option value="teacher">Giáo viên</option>
              <option value="admin">Quản trị viên</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="active">Hoạt động</option>
              <option value="inactive">Không hoạt động</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Người dùng
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vai trò
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ngày tạo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Đăng nhập cuối
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nguồn
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        PRO
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Hành động
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                          Không tìm thấy người dùng nào
                        </td>
                      </tr>
                    ) : (
                      users.map((userItem) => (
                        <tr key={userItem.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white font-bold text-sm">
                                  {userItem.name?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {userItem.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {userItem.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <select
                              value={userItem.role}
                              onChange={(e) => handleRoleChange(userItem.id, e.target.value as any)}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 ${getRoleColor(userItem.role)}`}
                            >
                              <option value="student">Học sinh</option>
                              <option value="teacher">Giáo viên</option>
                              <option value="admin">Quản trị viên</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {userItem.isActive ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-500" />
                              )}
                              <span className={`text-sm font-medium ${userItem.isActive ? 'text-green-600' : 'text-red-600'}`}>
                                {userItem.isActive ? 'Hoạt động' : 'Không hoạt động'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(userItem.createdAt).toLocaleDateString('vi-VN')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {userItem.lastLoginAt ? new Date(userItem.lastLoginAt).toLocaleDateString('vi-VN') : 'Chưa đăng nhập'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const badge = getDomainBadge(userItem.source_domain);
                              return (
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                                  {badge.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(userItem.permissions as any)?.pro ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">
                                <Crown className="w-3 h-3" /> PRO
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleProToggle(userItem.id, !!(userItem.permissions as any)?.pro)}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${(userItem.permissions as any)?.pro
                                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                  : 'bg-gray-100 text-gray-600 hover:bg-yellow-100 hover:text-yellow-800'
                                  }`}
                                title={(userItem.permissions as any)?.pro ? 'Thu hồi PRO' : 'Kích hoạt PRO'}
                              >
                                <Crown className="w-3 h-3 inline mr-1" />
                                {(userItem.permissions as any)?.pro ? 'Thu hồi PRO' : 'Cấp PRO'}
                              </button>
                              <button
                                onClick={() => handleStatusChange(userItem.id, !userItem.isActive)}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${userItem.isActive
                                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                                  }`}
                              >
                                {userItem.isActive ? 'Vô hiệu hóa' : 'Kích hoạt'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-500">
                  Hiển thị {(page - 1) * 20 + 1}-{Math.min(page * 20, totalUsers)} của {totalUsers} người dùng
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic to show reasonable page numbers (e.g., current page centered) can be complex
                    // Simple logic: just show 1..5 or based on current page
                    let p = page - 2 + i;
                    if (page < 3) p = i + 1;
                    if (p > totalPages) return null;
                    if (p < 1) return null;

                    return (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1 border rounded ${page === p ? 'bg-blue-600 text-white' : 'hover:bg-gray-50'}`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sau
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
